package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.pluralCategoriesFor
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAvatar
import dev.lingvo.site.ds.LAvatarSize
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LConflictCard
import dev.lingvo.site.ds.LInfoIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal fun editorFilePath(state: TranslationEditorState, row: KeyRow): String =
    state.project?.files?.firstOrNull { it.id == row.key.fileId }?.path ?: "Unknown file"

@Composable
internal fun EditorDetails(state: TranslationEditorState, row: KeyRow) {
    val file = editorFilePath(state, row)
    val context = row.key.context?.takeIf(String::isNotBlank) ?: "No context provided"
    val pluralCategories = pluralCategoriesFor(state.locale)
        .joinToString(", ") { it.name.lowercase() }

    Column(
        EditorDetailsBodyStyle.toModifier()
            .attr("data-editor-details", "true")
            .attr("role", "region")
            .attr("aria-label", tf("editor.editor_details.for_key", "Details for {key}", "key" to row.key.name)),
    ) {
        EditorDetailField("Context", context)
        EditorDetailField(
            "Tags",
            if (row.key.tags.isEmpty()) null else row.key.tags,
        )
        EditorDetailField(
            "Maximum length",
            row.key.maxLength?.let { "$it characters" } ?: "No maximum",
        )
        EditorDetailField(
            "Plural key",
            if (row.key.isPlural) "Yes - $pluralCategories" else "No",
        )
        EditorDetailField("File", file, mono = true)
    }
}

@Composable
private fun EditorDetailField(
    label: String,
    value: String,
    mono: Boolean = false,
) {
    Column(EditorDetailsFieldStyle.toModifier()) {
        Div(EditorDetailsLabelStyle.toModifier().toAttrs()) { Text(label) }
        Div(
            EditorDetailsValueStyle.toModifier()
                .then(if (mono) Modifier.monoFont() else Modifier)
                .toAttrs(),
        ) {
            Text(value)
        }
    }
}

@Composable
private fun EditorDetailField(
    label: String,
    tags: List<String>?,
) {
    Column(EditorDetailsFieldStyle.toModifier()) {
        Div(EditorDetailsLabelStyle.toModifier().toAttrs()) { Text(label) }
        if (tags == null) {
            Div(EditorDetailsValueStyle.toModifier().toAttrs()) { Text(t("editor.editor_details.no_tags", "No tags")) }
        } else {
            Row(EditorDetailsTagsStyle.toModifier()) {
                tags.forEach { tag -> LChip(tag) }
            }
        }
    }
}

@Composable
internal fun EditorIdentity(
    state: TranslationEditorState,
    row: KeyRow,
    onOpenUtility: (EditorUtilitySurface, EditorFocusRef) -> Unit = { _, _ -> },
) {
    val file = editorFilePath(state, row)
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        Row(EditorIdentityStyle.toModifier()) {
            Row(EditorIdentityMetadataStyle.toModifier()) {
                Span(EditorMetaStyle.toModifier().monoFont().toAttrs()) {
                    Text(row.key.name)
                }
                Span(EditorMetaStyle.toModifier().toAttrs()) { Text("/") }
                Div(
                    EditorMetaStyle.toModifier()
                        .monoFont()
                        .toAttrs { attr("title", file) },
                ) {
                    Text(file)
                }
            }
            EditorProposalTrigger(state, row, onOpenUtility)
            Row(EditorIdentityActionsStyle.toModifier()) {
                LButton(
                    label = t("editor.editor_details.details", "Details"),
                    onClick = {
                        onOpenUtility(
                            EditorUtilitySurface.DETAILS,
                            EditorFocusRef.ElementId(EDITOR_DETAILS_TRIGGER_ID),
                        )
                    },
                    modifier = Modifier.attr("id", EDITOR_DETAILS_TRIGGER_ID),
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    // The same glyph the utility pane's Details tab carries
                    // (EditorUtilityPane.kt) — one idea, one mark. Without it this GHOST button was
                    // a borderless, glyphless word floating at the top-right of the writing column,
                    // which reads as stray text rather than as the control that opens the pane.
                    leadingIcon = { LInfoIcon() },
                )
                PresenceGroup(state, row.key.id)
            }
        }
        MachineAttribution(row)
    }
}

@Composable
private fun PresenceGroup(state: TranslationEditorState, keyId: String) {
    val others = state.presence[keyId].orEmpty()
    if (others.isEmpty()) return
    val visible = others.take(3)
    // The whole sentence, not five fragments. It used to be assembled from Kotlin templates —
    // "and", "and N others", the is/are agreement and both access hints were English literals no
    // extractor could reach — so a reader on a translated interface was told in English that
    // somebody else had their key open. The number agreement is `tPlural`'s job rather than an
    // inline `if`, because "is" and "are" is English's rule: Polish needs a third form at five and
    // Japanese needs none, and neither can be reached from a boolean.
    val names = when (others.size) {
        1 -> others.single().name
        2 -> tf(
            "editor.editor_details.presence_two",
            "{first} and {second}",
            "first" to others[0].name,
            "second" to others[1].name,
        )
        else -> tPlural(
            "editor.editor_details.presence_more",
            others.size - 1,
            "{first} and {count} other",
            "{first} and {count} others",
            "first" to others[0].name,
        )
    }
    val accessHint = if (state.capability.canWriteTranslations) {
        t("editor.editor_details.you_can_still_type", "You can still type.")
    } else {
        t("editor.editor_details.you_can_still_view_and_copy", "You can still view and copy this translation.")
    }
    val label = tPlural(
        "editor.editor_details.presence_editing",
        others.size,
        "{names} is editing this. {hint}",
        "{names} are editing this. {hint}",
        "names" to names,
        "hint" to accessHint,
    )
    Row(
        EditorPresenceStyle.toModifier()
            .attr("role", "group")
            .attr("aria-label", label),
    ) {
        visible.forEach { presence ->
            LAvatar(
                name = presence.name,
                modifier = Modifier.styleModifier {
                    property("background-color", presence.avatarColor)
                },
                size = LAvatarSize.SM,
            )
        }
        if (others.size > visible.size) {
            Span(EditorPresenceCountStyle.toModifier().toAttrs()) {
                Text("+${others.size - visible.size}")  // i18n-ignore — an overflow count, written as a plus and a number in every language
            }
        }
    }
}

@Composable
private fun MachineAttribution(row: KeyRow) {
    val target = row.target ?: return
    if (!isMachineUnreviewed(target)) return
    Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
        LBadge(
            target.mtProvider?.let { "Machine — ${it.displayName}" } ?: "Machine translation",
            variant = LBadgeVariant.WARNING,
        )
        Span(EditorMetaStyle.toModifier().toAttrs()) { Text(t("editor.editor_details.nobody_has_reviewed_this_yet", "Nobody has reviewed this yet.")) }
    }
}

@Composable
internal fun ConflictCard(state: TranslationEditorState, conflict: EditorConflict) {
    // **"Take theirs" is the one action on this surface that destroys work, and it used to do it on
    // a single click.** `resolveConflictTakeTheirs` drops the conflict, the in-memory draft and this
    // browser's persisted copy of it in one synchronous call, and nothing anywhere restores them —
    // while *clearing* a translation, which a person can simply retype, has had a confirmation all
    // along. Worse, the button sat as the neutral middle option directly under a sentence promising
    // the reader their text was safe.
    //
    // The step is a disclosure inside the card rather than an `LModal`: the editor's Tab trap and
    // its Escape handling only cover `EditorModalSurface`, so a dialog opened from here would be
    // one the keyboard walks straight out of. Revealing the confirmation beside the trigger also
    // keeps focus on the button that was pressed, which replacing the row would not.
    var confirmingTakeTheirs by remember(conflict.keyId) { mutableStateOf(false) }
    LConflictCard(
        title = t("editor.editor_details.someone_else_changed_this_translation", "Someone else changed this translation"),
        description = t("editor.editor_details.your_text_is_safe_choose_which", "Your text is safe. Choose which version to keep, or merge them by hand."),
        mineLabel = "Yours",
        theirsLabel = "Theirs, already saved",
        // The editor moves focus here (`enterConflict`), which announces the card on arrival. A live
        // region as well would say the same thing twice.
        announce = false,
        focusId = EDITOR_CONFLICT_TARGET_ID,
        // The editor's own hook, kept through the promotion: `EditorControllerDom` and the surface
        // contract tests both find the card by it, and a refactor that renames a DOM contract is a
        // refactor that changed behaviour.
        modifier = Modifier.attr("data-editor-conflict", "true"),
        mine = {
            PlaceholderText(
                value = conflict.mine.value.ifBlank { joinForms(conflict.mine.pluralForms) },
                modifier = Modifier.fillMaxWidth(),
            )
        },
        theirs = {
            PlaceholderText(
                value = conflict.theirs.value?.takeIf(String::isNotBlank)
                    ?: joinForms(conflict.theirs.pluralForms.orEmpty()),
                modifier = Modifier.fillMaxWidth(),
            )
        },
    ) {
        LButton(t("editor.editor_details.keep_mine", "Keep mine"), { state.resolveConflictKeepMine(conflict.keyId) })
        LButton(
            t("editor.editor_details.take_theirs", "Take theirs"),
            { confirmingTakeTheirs = !confirmingTakeTheirs },
            modifier = Modifier.attr("aria-expanded", confirmingTakeTheirs.toString()),
            variant = LButtonVariant.SECONDARY,
        )
        if (confirmingTakeTheirs) {
            // `width: 100%` inside the card's wrapping action row, so the cost takes its own line
            // above the button that pays it rather than reading as a caption on one of three.
            Div(EditorQaBlockerStyle.toModifier().toAttrs()) {
                Text(
                    t(
                        "editor.editor_details.take_theirs_cost",
                        "This discards the version you typed, here and in this browser's " +
                            "saved draft. It cannot be undone.",
                    ),
                )
            }
            LButton(
                t("editor.editor_details.discard_mine_take_theirs", "Discard mine, take theirs"),
                { state.resolveConflictTakeTheirs(conflict.keyId) },
                variant = LButtonVariant.DANGER,
            )
            LButton(
                t("editor.editor_details.keep_choosing", "Cancel"),
                { confirmingTakeTheirs = false },
                variant = LButtonVariant.GHOST,
            )
        }
        LButton(t("editor.editor_details.merge_by_hand", "Merge by hand"), { state.dismissConflict(conflict.keyId) }, variant = LButtonVariant.GHOST)
    }
}

private fun joinForms(forms: Map<PluralCategory, String>): String =
    forms.entries.joinToString(" · ") { (category, form) -> "${category.name.lowercase()}: $form" }
